import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { placeTip, type TipContent } from "./chartTipFormat";

/**
 * The chart hover tip (mock v12 `#tip`: one navy tip for every hover detail,
 * above the mark, flipped below when there is no room, kept inside the
 * viewport). A chart marks each bar, segment or point with `tipMark(...)`;
 * the nearest `ChartTips` (CardBody chart, ChartSvg, MoneyBars, … wrap
 * their content in one) shows the tip:
 *  - hover (desktop) and tap (phone) on a mark;
 *  - keyboard: Tab reaches the chart once (one mark is in the tab order),
 *    the arrow keys, Home and End move between marks, Escape closes;
 *  - screen readers read each mark's `aria-label` (the whole tip as one
 *    sentence).
 * Hidden by default: nothing renders until a mark is hovered or focused.
 */

const MARK = "*[data-tip-mark]";

const readTip = (el: Element): TipContent | null => {
  try {
    const c = JSON.parse(el.getAttribute("data-tip") ?? "") as TipContent;
    return typeof c?.head === "string" && Array.isArray(c.lines) ? c : null;
  } catch {
    return null;
  }
};

/** The mark's drawn box: its children minus any transparent hit area
 *  (`data-tip-hit`) and axis text, so the tip sits above the bar, not above
 *  the column. A mark with nothing drawn (a withheld value) uses the hit area. */
const anchorRect = (el: Element): DOMRect => {
  const hit = el.querySelector("*[data-tip-hit]");
  if (!hit) return el.getBoundingClientRect();
  let box: { l: number; t: number; r: number; b: number } | null = null;
  for (const c of Array.from(el.children)) {
    if (c.hasAttribute("data-tip-hit")) continue;
    if (c.tagName.toLowerCase() === "text" && !c.classList.contains("lab"))
      continue;
    const r = c.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    box = box
      ? {
          l: Math.min(box.l, r.left),
          t: Math.min(box.t, r.top),
          r: Math.max(box.r, r.right),
          b: Math.max(box.b, r.bottom),
        }
      : { l: r.left, t: r.top, r: r.right, b: r.bottom };
  }
  if (!box) return hit.getBoundingClientRect();
  return new DOMRect(box.l, box.t, box.r - box.l, box.b - box.t);
};

/** The navy box itself (also used inside a recharts `<Tooltip content>`). */
export const TipBox = React.forwardRef<
  HTMLDivElement,
  { tip: TipContent; id?: string; style?: React.CSSProperties; inline?: boolean }
>(({ tip, id, style, inline }, ref) => (
  <div
    ref={ref}
    id={id}
    role="tooltip"
    className="ds-tip"
    data-inline={inline ? "" : undefined}
    style={style}
  >
    <b>{tip.head}</b>
    {tip.lines.map((l, i) => (
      <span key={i}>{l}</span>
    ))}
  </div>
));
TipBox.displayName = "TipBox";

interface Shown {
  el: Element;
  tip: TipContent;
  pinned: boolean;
}

export const ChartTips: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const root = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const rove = useRef(0);
  const id = useId();
  const [shown, setShown] = useState<Shown | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const marks = useCallback(
    () =>
      Array.from(root.current?.querySelectorAll(MARK) ?? []).filter(
        (m) => m.closest("*[data-tip-host]") === root.current,
      ),
    [],
  );
  // A mark belongs to its nearest host, so hosts can nest safely.
  const markOf = (t: EventTarget | null): Element | null => {
    const el = t instanceof Element ? t.closest(MARK) : null;
    return el && el.closest("*[data-tip-host]") === root.current ? el : null;
  };
  const show = (el: Element, pinned: boolean) => {
    const tip = readTip(el);
    if (!tip) return;
    setPos(null);
    setShown({ el, tip, pinned });
  };
  const hide = useCallback(() => {
    setShown(null);
    setPos(null);
  }, []);

  // One mark in the tab order (roving tabindex). Runs after every render,
  // so a chart that redraws (grain change) keeps a reachable mark.
  useLayoutEffect(() => {
    const ms = marks();
    if (ms.length === 0) return;
    const at = shown ? ms.indexOf(shown.el) : -1;
    if (at >= 0) rove.current = at;
    if (rove.current >= ms.length) rove.current = 0;
    ms.forEach((m, i) =>
      m.setAttribute("tabindex", i === rove.current ? "0" : "-1"),
    );
  });

  useLayoutEffect(() => {
    if (!shown || !box.current) return;
    if (!shown.el.isConnected) return hide();
    const r = anchorRect(shown.el);
    const b = box.current.getBoundingClientRect();
    setPos(
      placeTip(
        r,
        { width: b.width, height: b.height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [shown, hide]);

  // A pinned tip (tap or focus) closes on a tap outside the chart, and any
  // tip closes on scroll (it is placed in viewport space).
  useEffect(() => {
    if (!shown) return;
    const out = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) hide();
    };
    document.addEventListener("pointerdown", out);
    window.addEventListener("scroll", hide, { passive: true, capture: true });
    return () => {
      document.removeEventListener("pointerdown", out);
      window.removeEventListener("scroll", hide, { capture: true });
    };
  }, [shown, hide]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const el = markOf(e.target);
    if (!el) return;
    if (e.key === "Escape") return hide();
    const ms = marks();
    const i = ms.indexOf(el);
    const next =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? Math.min(i + 1, ms.length - 1)
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? Math.max(i - 1, 0)
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? ms.length - 1
              : -1;
    if (next < 0) return;
    e.preventDefault();
    rove.current = next;
    (ms[next] as HTMLElement | SVGElement).focus();
  };

  return (
    <div
      ref={root}
      data-tip-host=""
      className={className ?? "ds-charttips"}
      onMouseOver={(e) => {
        const el = markOf(e.target);
        if (el) {
          if (el !== shown?.el) show(el, false);
        } else if (shown && !shown.pinned) hide();
      }}
      onMouseLeave={() => shown && !shown.pinned && hide()}
      onFocus={(e) => {
        const el = markOf(e.target);
        if (el) show(el, true);
      }}
      onBlur={(e) => {
        if (!markOf(e.relatedTarget)) hide();
      }}
      onClick={(e) => {
        const el = markOf(e.target);
        if (el) show(el, true);
      }}
      onKeyDown={onKeyDown}
    >
      {children}
      {shown &&
        createPortal(
          <TipBox
            ref={box}
            id={id}
            tip={shown.tip}
            style={{
              transform: pos ? `translate(${pos.x}px, ${pos.y}px)` : undefined,
              visibility: pos ? "visible" : "hidden",
            }}
          />,
          document.body,
        )}
    </div>
  );
};
